// Mercapoli - Rider Dashboard con DIGGII y Transparencia de Comisiones
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Package, MapPin, Clock, CheckCircle, Navigation,
  Bike, Bell, LogOut, DollarSign, TrendingUp, Wallet,
  CreditCard, ChevronRight, Star, Zap, Settings, Coins,
  RefreshCw, AlertCircle, Radio
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { rider as riderApi, pedidos as pedidosApi, tracking as trackingApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import DiggiiBalance from '../../components/DiggiiBalance';
import DiggiiHistory from '../../components/DiggiiHistory';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_mercadelivery-1/artifacts/5y038jeg_icono_mercapoli.png";

const RiderDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [enLinea, setEnLinea] = useState(true);
  const [trackingActivo, setTrackingActivo] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [pedidosDisponibles, setPedidosDisponibles] = useState([]);
  const [misPedidos, setMisPedidos] = useState([]);
  const [activeTab, setActiveTab] = useState('pedidos');
  const [showDiggiiHistory, setShowDiggiiHistory] = useState(false);
  const [pedidoActivo, setPedidoActivo] = useState(null);

  // Enviar ubicación al servidor
  const enviarUbicacion = useCallback(async () => {
    if (!trackingActivo || !pedidoActivo) return;
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            await trackingApi.updateRiderUbicacion(
              position.coords.latitude,
              position.coords.longitude,
              pedidoActivo
            );
          } catch (error) {
            console.error('Error enviando ubicación:', error);
          }
        },
        (error) => console.error('Error GPS:', error),
        { enableHighAccuracy: true }
      );
    }
  }, [trackingActivo, pedidoActivo]);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 15000);
    return () => clearInterval(interval);
  }, [enLinea]);

  // Enviar ubicación cada 10 segundos cuando tracking está activo
  useEffect(() => {
    let trackingInterval;
    if (trackingActivo && pedidoActivo) {
      enviarUbicacion(); // Enviar inmediatamente
      trackingInterval = setInterval(enviarUbicacion, 10000);
    }
    return () => {
      if (trackingInterval) clearInterval(trackingInterval);
    };
  }, [trackingActivo, pedidoActivo, enviarUbicacion]);

  const loadData = async () => {
    try {
      // Cargar dashboard con estadísticas y DIGGII
      const dashData = await riderApi.getDashboard();
      setDashboard(dashData);
      
      if (enLinea) {
        const disponibles = await riderApi.getPedidosDisponibles();
        setPedidosDisponibles(disponibles);
      }
      
      // Cargar mis pedidos
      const pedidos = await riderApi.getPedidos();
      setMisPedidos(pedidos);
      
      // Verificar si hay pedido en camino para activar tracking
      const enCamino = pedidos.find(p => p.estado === 'en_camino');
      if (enCamino) {
        setPedidoActivo(enCamino.pedido_id);
        setTrackingActivo(true);
      }
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  };

  const aceptarPedido = async (pedidoId) => {
    try {
      await pedidosApi.updateEstado(pedidoId, 'en_camino');
      toast.success('¡Pedido aceptado! Tracking activado.');
      setPedidoActivo(pedidoId);
      setTrackingActivo(true);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aceptar pedido');
    }
  };

  const entregarPedido = async (pedidoId) => {
    try {
      await pedidosApi.updateEstado(pedidoId, 'entregado');
      toast.success('¡Pedido entregado!');
      setTrackingActivo(false);
      setPedidoActivo(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al marcar entrega');
    }
  };

  const simbolo = dashboard?.simbolo || 'Q';
  const formatMoney = (amount) => `${simbolo}${amount?.toLocaleString('es-GT', { minimumFractionDigits: 2 }) || '0.00'}`;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getEstadoBadge = (estado) => {
    const configs = {
      'listo': { color: 'bg-yellow-100 text-yellow-800', label: 'Listo para recoger' },
      'en_camino': { color: 'bg-blue-100 text-blue-800', label: 'En camino' },
      'entregado': { color: 'bg-green-100 text-green-800', label: 'Entregado' },
    };
    const config = configs[estado] || { color: 'bg-gray-100', label: estado };
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Mercapoli" className="h-8 w-8" />
              <div>
                <h1 className="font-heading font-bold">Rider</h1>
                <p className="text-xs text-muted-foreground">{user?.nombre || 'Mi Panel'}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Toggle En Línea */}
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted">
                <Switch 
                  checked={enLinea} 
                  onCheckedChange={setEnLinea}
                  className="data-[state=checked]:bg-green-500"
                />
                <span className={`text-sm font-medium ${enLinea ? 'text-green-600' : 'text-muted-foreground'}`}>
                  {enLinea ? 'En línea' : 'Desconectado'}
                </span>
              </div>
              
              <Button variant="ghost" size="icon" onClick={loadData}>
                <RefreshCw className="h-5 w-5" />
              </Button>
              
              <Button variant="ghost" size="icon" onClick={handleLogout}>
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Stats rápidos */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Card className="bg-gradient-to-br from-brand-primary to-brand-secondary text-white">
            <CardContent className="p-3 text-center">
              <DollarSign className="h-5 w-5 mx-auto mb-1 opacity-80" />
              <p className="text-lg font-bold">{formatMoney(dashboard?.ganancias_hoy || 0)}</p>
              <p className="text-xs opacity-80">Hoy</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <Bike className="h-5 w-5 mx-auto mb-1 text-brand-primary" />
              <p className="text-lg font-bold">{dashboard?.entregas_hoy || 0}</p>
              <p className="text-xs text-muted-foreground">Entregas hoy</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <TrendingUp className="h-5 w-5 mx-auto mb-1 text-green-500" />
              <p className="text-lg font-bold">{formatMoney(dashboard?.ganancias_mes || 0)}</p>
              <p className="text-xs text-muted-foreground">Este mes</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <Coins className="h-5 w-5 mx-auto mb-1 text-amber-500" />
              <p className="text-lg font-bold">{dashboard?.diggii?.balance?.toFixed(1) || 0}</p>
              <p className="text-xs text-muted-foreground">DIGGII</p>
            </CardContent>
          </Card>
        </div>

        {/* Comisión Info */}
        <Card className="mb-6 border-blue-500/30 bg-blue-50 dark:bg-blue-950/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-blue-500" />
                <span className="text-sm">
                  Comisión de plataforma: <span className="font-bold">{dashboard?.comision_plataforma_porcentaje || 15}%</span> sobre tarifa de entrega
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* DIGGII Card */}
        {dashboard?.diggii && dashboard.diggii.balance > 0 && (
          <div className="mb-6">
            <DiggiiBalance
              balance={dashboard.diggii.balance}
              valorLocal={dashboard.diggii.valor_local}
              simbolo={simbolo}
              progresoSiguiente={dashboard.diggii.progreso_siguiente}
              faltaParaSiguiente={200 - dashboard.diggii.progreso_siguiente}
              ratioAcumulacion={200}
              onClick={() => setShowDiggiiHistory(true)}
            />
          </div>
        )}

        {/* Tabs principales */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="pedidos" className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              Pedidos ({pedidosDisponibles.length})
            </TabsTrigger>
            <TabsTrigger value="historial" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Mis Entregas
            </TabsTrigger>
          </TabsList>

          {/* Tab Pedidos Disponibles */}
          <TabsContent value="pedidos" data-testid="pedidos-tab">
            {!enLinea ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Bike className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">Estás desconectado</p>
                  <p className="text-sm text-muted-foreground">Activa tu estado para recibir pedidos</p>
                </CardContent>
              </Card>
            ) : pedidosDisponibles.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">No hay pedidos disponibles</p>
                  <p className="text-sm text-muted-foreground">Los nuevos pedidos aparecerán aquí</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {pedidosDisponibles.map((pedido) => (
                  <motion.div
                    key={pedido.pedido_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Card className="border-green-500/30">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-bold">{pedido.restaurante_nombre}</p>
                            <p className="text-sm text-muted-foreground flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {pedido.restaurante_direccion}
                            </p>
                          </div>
                          {getEstadoBadge(pedido.estado)}
                        </div>
                        
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                          <Navigation className="h-4 w-4" />
                          <span>Entregar en: {pedido.direccion_entrega}</span>
                        </div>
                        
                        {/* Desglose de ganancia */}
                        <div className="bg-muted p-3 rounded-lg mb-3">
                          <div className="flex justify-between text-sm">
                            <span>Tarifa de entrega:</span>
                            <span>{formatMoney(pedido.costo_envio)}</span>
                          </div>
                          <div className="flex justify-between text-sm text-red-500">
                            <span>Comisión plataforma ({dashboard?.comision_plataforma_porcentaje}%):</span>
                            <span>-{formatMoney(pedido.costo_envio * (dashboard?.comision_plataforma_porcentaje || 15) / 100)}</span>
                          </div>
                          <div className="flex justify-between font-bold text-green-600 border-t mt-2 pt-2">
                            <span>Tu ganancia:</span>
                            <span>{formatMoney(pedido.estimacion_ganancia)}</span>
                          </div>
                        </div>
                        
                        <Button 
                          className="w-full bg-green-500 hover:bg-green-600"
                          onClick={() => aceptarPedido(pedido.pedido_id)}
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Aceptar · {formatMoney(pedido.estimacion_ganancia)}
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Tab Mis Entregas */}
          <TabsContent value="historial">
            {misPedidos.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Clock className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">No tienes entregas aún</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {misPedidos.map((pedido) => (
                  <Card key={pedido.pedido_id}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium">{pedido.restaurante_nombre || 'Pedido'}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(pedido.created_at).toLocaleDateString('es-GT')}
                          </p>
                        </div>
                        {getEstadoBadge(pedido.estado)}
                      </div>
                      
                      <p className="text-sm text-muted-foreground mb-3">
                        <MapPin className="h-3 w-3 inline mr-1" />
                        {pedido.direccion_entrega}
                      </p>
                      
                      {/* Desglose detallado */}
                      {pedido.desglose_rider && (
                        <div className="bg-muted p-3 rounded-lg text-sm">
                          <div className="flex justify-between">
                            <span>Tarifa entrega:</span>
                            <span>{formatMoney(pedido.desglose_rider.tarifa_entrega)}</span>
                          </div>
                          <div className="flex justify-between text-red-500">
                            <span>Comisión ({pedido.desglose_rider.comision_porcentaje}%):</span>
                            <span>-{formatMoney(pedido.desglose_rider.comision_plataforma)}</span>
                          </div>
                          <div className="flex justify-between font-bold text-green-600 border-t mt-1 pt-1">
                            <span>Tu ganancia:</span>
                            <span>{formatMoney(pedido.desglose_rider.ganancia_neta)}</span>
                          </div>
                        </div>
                      )}
                      
                      {pedido.estado === 'en_camino' && (
                        <Button 
                          className="w-full mt-3"
                          onClick={() => entregarPedido(pedido.pedido_id)}
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Marcar como entregado
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* DIGGII History Modal */}
      <DiggiiHistory 
        isOpen={showDiggiiHistory} 
        onClose={() => setShowDiggiiHistory(false)} 
      />
    </div>
  );
};

export default RiderDashboard;
